import React from 'react';
import { Chip } from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Science as ExperimentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Status Icon Component
 */
export const StatusIcon = ({ success }) => {
  return success ? (
    <SuccessIcon color="success" />
  ) : (
    <ErrorIcon color="error" />
  );
};

/**
 * Status Chip Component
 */
export const StatusChip = ({ success }) => {
  const theme = useTheme();
  return (
    <Chip
      label={success ? 'Success' : 'Failed'}
      color={success ? 'success' : 'error'}
      size="small"
      sx={{
        width: 80,
        height: 24,
        justifyContent: 'center',
        '& .MuiChip-label': {
          paddingLeft: '8px',
          paddingRight: '8px',
          fontSize: '0.75rem',
          fontWeight: 500,
        },
        ...(success
          ? {
              backgroundColor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
              },
            }
          : {
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }),
      }}
    />
  );
};

/**
 * Experiment Chip Component
 */
export const ExperimentChip = ({ isExperiment, treatmentName }) => {
  // Fixed dimensions to ensure both chips are exactly the same size
  const chipSx = {
    width: 120,
    height: 24,
    padding: 0,
    boxSizing: 'border-box',
    '& .MuiChip-label': {
      paddingLeft: isExperiment ? '4px' : '12px',
      paddingRight: '12px',
      fontSize: '0.75rem',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiChip-icon': {
      marginLeft: '8px',
      marginRight: '4px',
      width: 16,
      height: 16,
      flexShrink: 0,
    },
  };

  if (!isExperiment) {
    return <Chip label="Regular" color="default" size="small" sx={chipSx} />;
  }

  return (
    <Chip
      icon={<ExperimentIcon />}
      label={`${treatmentName || 'Experiment'}`}
      color="secondary"
      size="small"
      sx={chipSx}
    />
  );
};

/**
 * Sort Icon Component
 */
export const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy !== column) {
    return null;
  }
  return sortOrder === 'asc' ? (
    <ArrowUpwardIcon
      fontSize="small"
      sx={{ ml: 0.5, verticalAlign: 'middle' }}
    />
  ) : (
    <ArrowDownwardIcon
      fontSize="small"
      sx={{ ml: 0.5, verticalAlign: 'middle' }}
    />
  );
};
